import classNames from 'classnames';
import { Mail as MailIcon } from 'react-feather';
import { useTranslation } from 'react-i18next';

import { useLocale } from '../../../Shared/locale';
import { Mail, Product, Project, ProjectType } from '../../../Shared/types';
import { Card } from '../../../Shared/UI/Card';
import Delimit from '../../../Shared/UI/Delimit';
import { Icon } from '../../../Shared/UI/Icon';
import { useRouter } from '../../router';

export default function MailCard({
  mail,
  project,
}: {
  mail: Mail & {
    products: Product[];
    sent_count: number;
    complete: boolean;
  };
  project: Project;
}) {
  const { t } = useTranslation();
  const { routes } = useRouter();
  const { formatCurrency, formatDate, formatNumber } = useLocale();

  const muted = !mail.enabled && 'opacity-50';

  return (
    <Card
      spacing="xs"
      link={{
        href: routes.edit_custom_mail_page(project.id, mail.id),
        ariaLabel: mail.title,
      }}
      key={mail.id}
    >
      <div className="grid grid-cols-[8fr_2fr_1fr] items-center">
        <div>
          <h4 className={classNames(muted)}>
            <Icon className="mr-1">
              <MailIcon />
            </Icon>
            {mail.title}
          </h4>
          <div className="text-sm text-slate-400">
            <Delimit>
              {mail.start_at && (
                <>
                  {mail.end_at ? t('backend:continuous') : t('backend:once')}
                  {!mail.end_at && ` (${formatDate(mail.start_at)})`}
                </>
              )}
              {mail.include_individuals && mail.include_teams && (
                t('backend:individuals_and_teams')
              )}
              {mail.include_individuals && !mail.include_teams && (
                t('backend:individuals')
              )}
              {!mail.include_individuals && mail.include_teams && (
                t('backend:teams')
              )}
              {mail.min_days_old !== null && (
                t('backend:after_x_days', {
                  count: mail.min_days_old,
                  value: formatNumber(mail.min_days_old),
                })
              )}
              {mail.days_before_start !== null && (
                t(project.type === ProjectType.Event && project.default ? 'backend:x_days_before_event' : 'backend:x_days_before_campaign', {
                  count: mail.days_before_start,
                  value: formatNumber(mail.days_before_start),
                })
              )}
              {mail.days_after_end !== null && (
                t(project.type === ProjectType.Event && project.default ? 'backend:x_days_after_event' : 'backend:x_days_after_campaign', {
                  count: mail.days_after_end,
                  value: formatNumber(mail.days_after_end),
                })
              )}
              {mail.created_after !== null && (
                t('backend:created_after_value', {
                  value: formatDate(mail.created_after, 'display_date_time'),
                })
              )}
              {mail.max_funds_raised !== null && (
                <>
                  {(mail.min_funds_raised || 0) === mail.max_funds_raised && (
                    t('backend:raised_exactly', {
                      value: formatCurrency(mail.max_funds_raised),
                    })
                  )}
                  {(mail.min_funds_raised || 0) !== mail.max_funds_raised && (
                    t('backend:raised_range', {
                      min: formatCurrency(mail.min_funds_raised || 0),
                      max: formatCurrency(mail.max_funds_raised),
                    })
                  )}
                </>
              )}
              {mail.min_funds_raised !== null && mail.max_funds_raised === null && (
                t('backend:raised_at_least', {
                  min: formatCurrency(mail.min_funds_raised),
                })
              )}
              {mail.products.length > 0 && t('backend:n_products', {
                count: mail.products.length,
                value: formatNumber(mail.products.length),
              })}
            </Delimit>
          </div>
        </div>
        <div className="text-sm">
          <div className="font-medium">
            {formatNumber(mail.sent_count)}×
          </div>
          <div className="text-slate-400">
            {t('backend:sent')}
          </div>
        </div>
        <div className={classNames('text-right font-medium', mail.enabled ? 'text-emerald-600' : 'text-slate-500')}>
          {mail.enabled ? t('backend:active') : (!mail.complete ? t('backend:draft') : t('backend:inactive'))}
        </div>
      </div>
    </Card>
  );
}
